// Badges der Pricing-Sperre und des Vertrags für Kundenseite und
// Migrations-Übersicht.
// PricingEnforcementBadge zeigt den Status der Sperre für einen Kunden, als
// Pille mit optionaler Unterzeile. Der Zustand kommt aus
// pricingEnforcementState (src/helper/pricing-flag.js), die Texte aus
// ENFORCEMENT_LABELS.
// PaymentKindBadge zeigt die Art eines Payments (Paket, Altvertrag, Trial,
// Credits, Unbekannter Plan), contractOf den Vertrag eines Kunden.
import { ArrowPathIcon } from '@heroicons/react/20/solid'

import { classNames } from '../../helper'
import {
  PAYMENT_KINDS,
  UNKNOWN_PLAN_LABEL,
  describePlanValue,
  formatCount,
  paymentKind,
  planName
} from '../../helper/pricing'
import {
  ENFORCEMENT_LABELS,
  ENFORCEMENT_STATES
} from '../../helper/pricing-flag'
import {
  planOf,
  selectPayments
} from '../../helper/pricing-mirror/payment-selection'

const BADGE =
  'inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset'

// ---------------------------------------------------------------------------
// Art eines Payments und Vertrag eines Kunden
// ---------------------------------------------------------------------------

export const NO_PAYMENT_KIND = 'none'

const KIND_TONES = {
  [PAYMENT_KINDS.PLAN]: 'bg-indigo-50 text-indigo-700 ring-indigo-700/10',
  [PAYMENT_KINDS.LEGACY]: 'bg-gray-50 text-gray-700 ring-gray-500/20',
  [PAYMENT_KINDS.TRIAL]: 'bg-purple-50 text-purple-700 ring-purple-700/10',
  [PAYMENT_KINDS.CREDITS]: 'bg-sky-50 text-sky-700 ring-sky-700/10',
  [PAYMENT_KINDS.UNKNOWN_PLAN]: 'bg-red-50 text-red-700 ring-red-600/20',
  [NO_PAYMENT_KIND]: 'bg-white text-gray-500 ring-gray-300'
}

/**
 * Text, Farbe und Hinweis zur Art eines Payments, rein und ohne React. Ohne
 * Payment heißt es „Keine Zahlung".
 *
 * @returns {{ kind: string, label: string, title: string|undefined,
 *   tone: string }}
 */
export const paymentKindBadgeModel = (payment) => {
  if (!payment) {
    return {
      kind: NO_PAYMENT_KIND,
      label: 'Keine Zahlung',
      title: undefined,
      tone: KIND_TONES[NO_PAYMENT_KIND]
    }
  }

  const kind = paymentKind(payment)
  const labels = {
    [PAYMENT_KINDS.PLAN]: `Paket · ${planName(planOf(payment))}`,
    [PAYMENT_KINDS.LEGACY]: 'Altvertrag',
    [PAYMENT_KINDS.TRIAL]: 'Trial',
    [PAYMENT_KINDS.CREDITS]: 'Credits',
    [PAYMENT_KINDS.UNKNOWN_PLAN]: UNKNOWN_PLAN_LABEL
  }
  const titles = {
    [PAYMENT_KINDS.LEGACY]: 'Payment ohne Paket, Verhalten wie bisher',
    [PAYMENT_KINDS.UNKNOWN_PLAN]: `plan „${describePlanValue(payment.plan).raw}“ ist kein bekanntes Paket. Das Dashboard behandelt das Payment wie einen Altvertrag.`
  }

  return {
    kind,
    label: labels[kind],
    title: titles[kind],
    tone: KIND_TONES[kind]
  }
}

export const PaymentKindBadge = ({ payment, className }) => {
  const model = paymentKindBadgeModel(payment)
  return (
    <span
      className={classNames(BADGE, model.tone, className)}
      title={model.title}
      data-kind={model.kind}
    >
      {model.label}
    </span>
  )
}

/**
 * Vertrag eines Kunden. Mit Paket-Zahlung gilt die neueste davon, sonst das
 * Laufzeit-Payment (neuestes ohne Credits). Nur Credits ergibt credits,
 * keine Payments none.
 *
 * @returns {{ kind: string, payment: object|null, planId: string|null }}
 */
export const contractOf = (payments, planById) => {
  const list = Array.isArray(payments) ? payments.filter(Boolean) : []
  const { planPayment, periodPayment } = selectPayments(list, planById)

  if (planPayment) {
    return {
      kind: PAYMENT_KINDS.PLAN,
      payment: planPayment,
      planId: planOf(planPayment, planById)
    }
  }
  if (periodPayment) {
    return {
      kind: paymentKind(periodPayment),
      payment: periodPayment,
      planId: null
    }
  }
  if (list.length) {
    return { kind: PAYMENT_KINDS.CREDITS, payment: list[0], planId: null }
  }
  return { kind: NO_PAYMENT_KIND, payment: null, planId: null }
}

const addonPart = (n, one, other) =>
  n > 0 ? `${formatCount(n)} ${n === 1 ? one : other}` : null

/**
 * Empfehlung aus recommendPlan als Text, z. B. „Growth mit 3 Zusatzstellen
 * und 1 Zusatzzugang".
 */
export const recommendationLabel = (recommendation) => {
  if (!recommendation) return null
  const parts = [
    addonPart(recommendation.addonCareers, 'Zusatzstelle', 'Zusatzstellen'),
    addonPart(recommendation.addonSeats, 'Zusatzzugang', 'Zusatzzugängen')
  ].filter(Boolean)
  const name = planName(recommendation.planId)
  return parts.length ? `${name} mit ${parts.join(' und ')}` : name
}

// ---------------------------------------------------------------------------
// Pricing-Sperre
// ---------------------------------------------------------------------------

const TONES = {
  [ENFORCEMENT_STATES.ACTIVE]: 'bg-green-50 text-green-700 ring-green-600/20',
  [ENFORCEMENT_STATES.READY]: 'bg-indigo-50 text-indigo-700 ring-indigo-700/10',
  [ENFORCEMENT_STATES.WAITING]: 'bg-amber-50 text-amber-800 ring-amber-600/20',
  [ENFORCEMENT_STATES.OFF]: 'bg-gray-50 text-gray-600 ring-gray-500/10',
  [ENFORCEMENT_STATES.PARTIAL]: 'bg-cyan-50 text-cyan-700 ring-cyan-700/10',
  [ENFORCEMENT_STATES.UNKNOWN]: 'bg-red-50 text-red-700 ring-red-600/20',
  [ENFORCEMENT_STATES.PLAN_UNKNOWN]: 'bg-red-50 text-red-700 ring-red-600/20'
}

const DOTS = {
  [ENFORCEMENT_STATES.ACTIVE]: 'fill-green-500',
  [ENFORCEMENT_STATES.READY]: 'fill-indigo-500',
  [ENFORCEMENT_STATES.WAITING]: 'fill-amber-500',
  [ENFORCEMENT_STATES.OFF]: 'fill-gray-400',
  [ENFORCEMENT_STATES.PARTIAL]: 'fill-cyan-500',
  [ENFORCEMENT_STATES.UNKNOWN]: 'fill-red-500',
  [ENFORCEMENT_STATES.PLAN_UNKNOWN]: 'fill-red-500'
}

/**
 * Texte und Farbe, rein und ohne React. Ein unbekannter Zustand gilt als
 * unknown, damit nie still „aus" angezeigt wird.
 *
 * @param {{ state: string, planName?: string|null, percent?: number|null }} params
 * @returns {{ state: string, label: string, detail: string, tone: string,
 *   dot: string }}
 */
export const enforcementBadgeModel = ({
  state,
  planName = null,
  percent = null
} = {}) => {
  const known = Object.prototype.hasOwnProperty.call(ENFORCEMENT_LABELS, state)
  const resolved = known ? state : ENFORCEMENT_STATES.UNKNOWN
  const texts = ENFORCEMENT_LABELS[resolved]

  let label = texts.label
  let detail = texts.detail

  if (resolved === ENFORCEMENT_STATES.ACTIVE && planName) {
    detail = `Paket ${planName} wird im Dashboard durchgesetzt.`
  }
  if (
    resolved === ENFORCEMENT_STATES.PARTIAL &&
    typeof percent === 'number' &&
    Number.isFinite(percent)
  ) {
    label = `Teil-Rollout ${percent} %`
  }

  return {
    state: resolved,
    label,
    detail,
    tone: TONES[resolved],
    dot: DOTS[resolved]
  }
}

/**
 * @param {object} props
 * @param {string} props.state aus pricingEnforcementState
 * @param {string} [props.planName] Name des Pakets, für die Unterzeile bei
 *   active
 * @param {number|null} [props.percent] Prozent bei partial
 * @param {boolean} [props.showDetail] Unterzeile anzeigen, sonst steht sie im
 *   title der Pille
 * @param {boolean} [props.isLoading] Platzhalter, solange FEATURE_FLAGS lädt
 * @param {() => void} [props.onRetry] Neu laden bei unknown
 * @param {boolean} [props.isRetrying]
 */
const PricingEnforcementBadge = ({
  state,
  planName,
  percent,
  showDetail = false,
  isLoading = false,
  onRetry,
  isRetrying = false,
  className
}) => {
  if (isLoading) {
    return (
      <span
        className={classNames(
          'inline-block h-6 w-32 animate-pulse rounded-md bg-gray-200',
          className
        )}
        aria-label='Status der Pricing-Sperre wird geladen'
      />
    )
  }

  const model = enforcementBadgeModel({ state, planName, percent })
  const canRetry = model.state === ENFORCEMENT_STATES.UNKNOWN && !!onRetry

  return (
    <div className={className}>
      <div className='flex flex-wrap items-center gap-2'>
        <span
          className={classNames(BADGE, model.tone)}
          title={showDetail ? undefined : model.detail}
          data-state={model.state}
        >
          <svg
            viewBox='0 0 6 6'
            aria-hidden='true'
            className={classNames('h-1.5 w-1.5', model.dot)}
          >
            <circle r={3} cx={3} cy={3} />
          </svg>
          {model.label}
        </span>
        {canRetry && (
          <button
            type='button'
            onClick={onRetry}
            disabled={isRetrying}
            className='inline-flex items-center gap-x-1 text-xs font-medium text-indigo-600 hover:text-indigo-500 disabled:opacity-50'
          >
            <ArrowPathIcon
              className={classNames(
                'h-3.5 w-3.5',
                isRetrying && 'animate-spin'
              )}
              aria-hidden='true'
            />
            Erneut laden
          </button>
        )}
      </div>
      {showDetail && (
        <p className='mt-1 text-xs text-gray-500'>{model.detail}</p>
      )}
    </div>
  )
}

export default PricingEnforcementBadge
